import { createElement, type ComponentType } from "react";
import { billingVisible } from "../billing/release";

/**
 * Every code-split route, in one list, with the import that fetches it.
 *
 * It used to be a column of `lazy(() => import(...))` consts in `App.tsx`, which
 * was fine until something other than the router needed to know what a path
 * loads. Screen transitions do: the cover is the moment to start fetching the
 * chunk the player is heading for, so that a second of cards hides the load
 * instead of blowing off to reveal "Loading…".
 *
 * One list rather than a second copy next to the first, because a preload map
 * that drifted from the route table would fetch the wrong chunk and look exactly
 * like it was working.
 */
interface RouteSource {
  /** The router path, `:param` segments and all. */
  path: string;
  load: () => Promise<{ default: ComponentType<Record<string, never>> }>;
}

/**
 * Pixi is the reason any of this is split: nothing but the play screens touches
 * the table renderer. Firebase is deliberately NOT among them — the home
 * screen's account chip and the cloud save started at launch both need it.
 */
const SOURCES: readonly RouteSource[] = [
  { path: "/local/setup", load: () => import("./screens/LocalSetupScreen").then((m) => ({ default: m.LocalSetupScreen })) },
  { path: "/local/play", load: () => import("./screens/GameScreen").then((m) => ({ default: m.GameScreen })) },
  { path: "/local/leaderboard", load: () => import("./screens/LeaderboardScreen").then((m) => ({ default: m.LeaderboardScreen })) },
  { path: "/themes", load: () => import("./screens/ThemeEditorScreen").then((m) => ({ default: m.ThemeEditorScreen })) },
  { path: "/stats", load: () => import("./screens/StatsScreen").then((m) => ({ default: m.StatsScreen })) },
  { path: "/tutorial", load: () => import("./screens/TutorialScreen").then((m) => ({ default: m.TutorialScreen })) },
  { path: "/tutorial/:tutorialId", load: () => import("./screens/TutorialScreen").then((m) => ({ default: m.TutorialScreen })) },
  { path: "/campaign", load: () => import("./screens/CampaignMapScreen").then((m) => ({ default: m.CampaignMapScreen })) },
  {
    path: "/campaign/play/:levelId",
    load: () => import("./screens/CampaignPlayScreen").then((m) => ({ default: m.CampaignPlayScreen })),
  },
  { path: "/account", load: () => import("./screens/AccountScreen").then((m) => ({ default: m.AccountScreen })) },
  // Not a route at all in production, rather than one that goes nowhere.
  ...(billingVisible()
    ? [{ path: "/premium", load: () => import("./screens/PremiumScreen").then((m) => ({ default: m.PremiumScreen })) }]
    : []),
  { path: "/online", load: () => import("./screens/OnlineHomeScreen").then((m) => ({ default: m.OnlineHomeScreen })) },
  { path: "/join/:code", load: () => import("./screens/JoinLinkScreen").then((m) => ({ default: m.JoinLinkScreen })) },
  { path: "/online/lobby/:gameId", load: () => import("./screens/OnlineLobbyScreen").then((m) => ({ default: m.OnlineLobbyScreen })) },
  { path: "/online/play/:gameId", load: () => import("./screens/OnlinePlayScreen").then((m) => ({ default: m.OnlinePlayScreen })) },
  { path: "/online/watch/:gameId", load: () => import("./screens/OnlineWatchScreen").then((m) => ({ default: m.OnlineWatchScreen })) },
  {
    path: "/online/leaderboard/:gameId",
    load: () => import("./screens/OnlineLeaderboardScreen").then((m) => ({ default: m.OnlineLeaderboardScreen })),
  },
];

type RouteComponent = ComponentType<Record<string, never>>;

export interface AppRoute extends RouteSource {
  /** What the router renders. Synchronous once the chunk is in; see `routeComponent`. */
  Component: RouteComponent;
  /** Fetches and evaluates the chunk. Memoised; resolves whether or not it failed. */
  preload: () => Promise<void>;
  /** Whether the chunk is already in, so rendering the route cannot suspend. */
  ready: () => boolean;
}

/**
 * A route component that renders SYNCHRONOUSLY once its chunk has loaded.
 *
 * `React.lazy` is not that: its first render always suspends, even when the
 * import it wraps resolved long ago, because it only learns so through a
 * `.then` that runs a microtask later. So a screen whose code was preloaded
 * still rendered the "Loading…" fallback for a frame and then remounted - in the
 * middle of a slide, which is exactly the first-use stutter preloading was
 * meant to remove. This holds the resolved component and renders it directly;
 * only a route that genuinely has not arrived throws its promise to Suspense.
 *
 * A failed load is forgotten rather than cached, so the next render (or the next
 * preload) tries again instead of replaying the same rejection forever.
 */
function routeComponent(load: RouteSource["load"]) {
  let resolved: RouteComponent | null = null;
  let inFlight: Promise<void> | null = null;
  const fetchChunk = (): Promise<void> =>
    (inFlight ??= load().then(
      (m) => {
        resolved = m.default;
      },
      (err: unknown) => {
        inFlight = null;
        throw err;
      },
    ));
  function Route(props: Record<string, never>) {
    if (resolved) return createElement(resolved, props);
    throw fetchChunk();
  }
  return {
    Component: Route,
    preload: () => fetchChunk().catch(() => {
      // A failed preload is not a failed navigation: the route tries the same
      // import again when it renders, and Suspense shows its fallback if it must.
    }),
    ready: () => resolved !== null,
  };
}

/** Built once, here, so a route's component identity is stable across renders. */
export const ROUTES: readonly AppRoute[] = SOURCES.map((r) => ({ ...r, ...routeComponent(r.load) }));

function routeFor(path: string): AppRoute | undefined {
  return ROUTES.find((r) => matches(r.path, path));
}

/**
 * Starts fetching the chunk a concrete path belongs to, and resolves once it is
 * in (or has failed). Idempotent. A path that matches nothing - the home screen,
 * a typo - resolves at once: there is nothing to fetch.
 */
export function preloadRoute(path: string): Promise<void> {
  return routeFor(path)?.preload() ?? Promise.resolve();
}

/**
 * The route a path belongs to, as written in the table (`/online/play/:gameId`),
 * or the path itself when no route matches. Feedback groups by this rather than
 * by the URL, which carries a different game id every time.
 */
export function routePattern(path: string): string {
  const bare = path.split("?")[0] ?? path;
  if (bare === "" || bare === "/") return "/";
  return routeFor(bare)?.path ?? bare;
}

/** Whether a path's screen can render without suspending. True for the home screen. */
export function isRouteReady(path: string): boolean {
  return routeFor(path)?.ready() ?? true;
}

/**
 * Loads every route's chunk in the background after launch, one per idle
 * callback, so the FIRST visit to any screen costs what every later one does.
 *
 * Measured with `scripts/transition-perf.mjs --latency=150`: fetching and
 * evaluating a screen's code on first use put a 100-125ms frame into the middle
 * of its slide, against ~35ms warm. The chunks are small (all of them together
 * are a fraction of the Pixi and Firebase chunks already loaded at launch), so
 * there is no reason to make a player's first tap pay for them.
 *
 * One at a time and on idle, never in a burst, so it cannot compete with the
 * first paint or a transition already running. Menu screens come first because
 * that is the order a player can reach them in.
 */
export function warmRoutes(): Promise<void> {
  const menu = ["/local/setup", "/online", "/themes", "/stats", "/account"];
  const rank = (r: AppRoute) => (menu.includes(r.path) ? menu.indexOf(r.path) : menu.length);
  const queue = [...ROUTES].sort((a, b) => rank(a) - rank(b));
  const ric = (globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
    .requestIdleCallback;
  const later = (fn: () => void) => (ric ? ric(fn, { timeout: 3000 }) : window.setTimeout(fn, 200));
  return new Promise((done) => {
    const next = () => {
      const route = queue.shift();
      if (!route) return done();
      void route.preload().then(() => later(next));
    };
    later(next);
  });
}
/** Segment-wise match, where a `:param` segment matches any single non-empty segment. */
function matches(pattern: string, path: string): boolean {
  const p = segments(pattern);
  const s = segments(path.split("?")[0] ?? path);
  if (p.length !== s.length) return false;
  return p.every((seg, i) => (seg.startsWith(":") ? s[i]!.length > 0 : seg === s[i]));
}

function segments(path: string): string[] {
  return path.split("/").filter(Boolean);
}
